import { Loader } from '@googlemaps/js-api-loader';
import { useTranslation } from 'next-i18next';
import { useEffect, useState, Dispatch, SetStateAction } from 'react';
import Link from 'next/link';
import styles from '@components/StoreCard/NearestStores.styles';
import StoreCard from '@components/StoreCard/StoreCard';
import SmallStoreCard from '@components/SmallStoreCard/SmallStoreCard';
import ErrorBoundary from '@components/errors/ErrorBoundary';
import Button from '@components/Button/Button';
import Icon from '@components/icons/Icon';
import { IUserLocation, IPointOfPresence, IGoogleMapLocation } from '@features/checkout/typescript';
import nextProductApi from '@features/api/nextApiService/nextProductService';
import NextGeoApi from '@features/api/nextApiService/nextGeoApi';
import { foundShowroomsByLocation, getClearUserLocation, getUserGeolocation, findShowroomLocationByCity, findPlaceFromQuery, filterByMerchandisingDisplayStatus, sortShowroomsByMerchandisingDisplayStatus } from '@utils/showroom';
import theme from '@styles/theme';
import getPublicEnv from '@utils/getPublicEnv';
import Checkbox from '@components/Checkbox/Checkbox';
import SearchShowroom from './SearchShowroom';
import Preloader from '@components/Preloader/Preloader';
type TPropsType = {
  productId: number;
  productName?: string;
  behaviour?: string;
  column?: boolean;
  setAsideActive?: Dispatch<SetStateAction<boolean>>;
  setFirstShowroom?: Dispatch<SetStateAction<IPointOfPresence | null>>;
  limit?: number;
  useUserGeolocationInQuery?: boolean;
  useMerchandisingDisplayStatuses?: boolean;
  isDip?: boolean;
};
const NearestStores = ({
  productId,
  productName,
  column = false,
  setAsideActive = undefined,
  setFirstShowroom = undefined,
  limit = 3,
  useUserGeolocationInQuery = true,
  useMerchandisingDisplayStatuses = false,
  isDip = false
}: TPropsType) => {
  const [showrooms, setShowrooms] = useState<IPointOfPresence[]>([]);
  const [userGeolocation, setUserGeolocation] = useState<IUserLocation>();
  const [foundShowrooms, setFoundShowrooms] = useState<IPointOfPresence[]>([]);
  const [info, setInfo] = useState<string>('');
  const [onlyAvailable, setOnlyAvailable] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [activeUuid, setActiveUuid] = useState<string>('');
  let showroomInfoTimeOut: NodeJS.Timeout | null;
  const getClearLocation = (): IGoogleMapLocation => ({
    lat: null,
    lng: null
  });
  const clearTimeOut = (timeOut: NodeJS.Timeout | null) => {
    if (timeOut) {
      clearTimeout(timeOut);
    }
  };
  const handleInfoTime = (time = 2000) => {
    if (info.length > 0) {
      clearTimeOut(showroomInfoTimeOut);
      showroomInfoTimeOut = setTimeout(() => {
        setInfo('');
      }, time);
    }
  };
  const getPointsGroups = () => {
    if (isDip) {
      return ['VOX_DIP, VOX_INTERIORS'];
    }
    return ['VOX_INTERIORS', 'VOXBOX_STUDIO', 'VOX_FURNITURES'];
  };
  const [searchLocation, setSearchLocation] = useState<IGoogleMapLocation>(getClearLocation());
  const {
    t
  } = useTranslation('common');
  const loader = new Loader({
    apiKey: getPublicEnv('NEXT_PUBLIC_GOOGLE_MAP_API_KEY'),
    version: 'weekly',
    libraries: ['places']
  });
  const findFirstStore = (nearestStores: IPointOfPresence[]): IPointOfPresence | undefined => {
    const firstStoreProductAvailable = nearestStores.find(store => store.merchandisingDisplayStatus === 'STATUS_AVAILABLE');
    if (firstStoreProductAvailable?.name) return firstStoreProductAvailable;
    const firstStoreProductLimitedAvailable = nearestStores.find(store => store.merchandisingDisplayStatus === 'STATUS_LIMITED_AVAILABILITY');
    if (firstStoreProductLimitedAvailable?.name) return firstStoreProductLimitedAvailable;
    const sortNearestStores = sortShowroomsByMerchandisingDisplayStatus(nearestStores);
    if (sortNearestStores.length > 0) return sortNearestStores[0];
    return undefined;
  };
  const handleFoundPoints = (location: IGoogleMapLocation, newOnlyAvailable: boolean) => {
    if (location.lat && location.lng) {
      const {
        showrooms: newFoundShowrooms
      } = foundShowroomsByLocation(location, showrooms, 300);
      setFoundShowrooms(newOnlyAvailable ? filterByMerchandisingDisplayStatus(newFoundShowrooms as IPointOfPresence[], 'STATUS_AVAILABLE') : [...sortShowroomsByMerchandisingDisplayStatus(newFoundShowrooms as IPointOfPresence[])] as IPointOfPresence[]);
    } else {
      setFoundShowrooms(newOnlyAvailable ? filterByMerchandisingDisplayStatus(showrooms, 'STATUS_AVAILABLE') : [...sortShowroomsByMerchandisingDisplayStatus(showrooms)] as IPointOfPresence[]);
    }
  };
  const searchShowrooms = async (newSearchWord: string | null, newLocation: IGoogleMapLocation | null = null, htmlDivElement: HTMLDivElement) => {
    if (showrooms.length > 0) {
      if (newSearchWord) {
        const resultLocationBycityName = findShowroomLocationByCity(showrooms, newSearchWord);
        if (resultLocationBycityName?.lat && resultLocationBycityName?.lng) {
          void searchShowrooms(null, resultLocationBycityName, htmlDivElement);
        } else {
          const resultLocation = await findPlaceFromQuery(htmlDivElement, newSearchWord);
          if (resultLocation) {
            void searchShowrooms(null, resultLocation, htmlDivElement);
          } else {
            setFoundShowrooms([]);
          }
        }
      } else if (newLocation && newLocation.lat && newLocation.lng) {
        setSearchLocation(newLocation);
        handleFoundPoints(newLocation, onlyAvailable);
      } else {
        setSearchLocation(getClearLocation());
        setFoundShowrooms(sortShowroomsByMerchandisingDisplayStatus(onlyAvailable ? [...filterByMerchandisingDisplayStatus(showrooms, 'STATUS_AVAILABLE')] : [...sortShowroomsByMerchandisingDisplayStatus(showrooms)]));
      }
      handleInfoTime(0);
    } else {
      setInfo(t('showroomsWait'));
    }
  };
  useEffect(() => {
    const fetchShowrooms = async (useMerchandisingDisplayStatusesInFetch: boolean, useUserGeolocationInQueryInFetch: boolean) => {
      setIsLoading(true);
      let nearestStores;
      let userLocation: IUserLocation | undefined;
      if (useUserGeolocationInQueryInFetch && (!userGeolocation || !userGeolocation?.location?.lat || !userGeolocation?.location?.lng)) {
        let newUserGeolocation = await getUserGeolocation(false);
        if (newUserGeolocation && newUserGeolocation?.lat && newUserGeolocation?.lng) {
          userLocation = {
            location: newUserGeolocation,
            city: null,
            country: null,
            postcode: null
          };
        } else {
          const geoUserGeolocation = await NextGeoApi.getGeoData();
          if (geoUserGeolocation && geoUserGeolocation?.latitude && geoUserGeolocation?.longitude) {
            userLocation = {
              location: {
                lat: +geoUserGeolocation.latitude,
                lng: +geoUserGeolocation.longitude
              },
              city: geoUserGeolocation.city || null,
              country: geoUserGeolocation.country || null,
              postcode: null
            };
          } else {
            newUserGeolocation = await getUserGeolocation();
            if (newUserGeolocation && newUserGeolocation?.lat && newUserGeolocation?.lng) {
              userLocation = {
                location: newUserGeolocation,
                city: null,
                country: null,
                postcode: null
              };
            }
          }
        }
        if (userLocation?.location?.lat && userLocation?.location?.lng) {
          setUserGeolocation(userLocation);
        }
      } else {
        userLocation = userGeolocation;
      }
      nearestStores = await nextProductApi.getNearestStores(productId, useUserGeolocationInQuery && userLocation && userLocation.location?.lat && userLocation.location?.lng ? userLocation?.location : null, limit, useMerchandisingDisplayStatusesInFetch, getPointsGroups());
      if (nearestStores && nearestStores.length > 0) {
        if (nearestStores.length > 1) {
          if (userLocation && userLocation.location?.lat && userLocation.location?.lng) {
            nearestStores = foundShowroomsByLocation(userLocation.location, nearestStores, 1000).showrooms;
          }
          nearestStores = sortShowroomsByMerchandisingDisplayStatus(nearestStores);
        }
        if (setFirstShowroom && nearestStores.length > 0 && showrooms.length === 0) {
          setFirstShowroom(findFirstStore(nearestStores) || null);
        }
        setShowrooms(nearestStores);
        setFoundShowrooms(nearestStores);
        setIsLoading(false);
      } else if (useUserGeolocationInQueryInFetch) {
        fetchShowrooms(useMerchandisingDisplayStatuses, false);
      } else {
        setIsLoading(false);
      }
    };
    if (showrooms.length <= 1) {
      fetchShowrooms(useMerchandisingDisplayStatuses, true);
    }
  }, [productId, limit, useMerchandisingDisplayStatuses]);
  return <ErrorBoundary>
      <section id="showroom_section" sx={styles.section(column, isLoading)}>
        {column && setAsideActive && <div sx={styles.titleContainer}>
            <Button variant="iconOnly" onClick={() => setAsideActive(false)}>
              <Icon icon="ArrowBack" size="16px" color={theme.rawColors.darkGrey700} />
            </Button>
            <Button variant="link" onClick={() => setAsideActive(false)}>
              <div sx={styles.title}>{t('productAvailabilityInShowrooms')}</div>
            </Button>
          </div>}
        <div sx={styles.storesContainer} data-cy="ProductNearestStores">
          <>
            <div sx={styles.subTitle}>{!isDip && t('frontNearestStoresInfo')}</div>
            <SearchShowroom loader={loader} userLocation={userGeolocation} setUserLocation={setUserGeolocation} searchShowrooms={searchShowrooms} getClearUserLocation={getClearUserLocation} />
            {!isDip && <Checkbox value={onlyAvailable} onChange={() => {
            setOnlyAvailable(!onlyAvailable);
            handleFoundPoints(searchLocation?.lat && searchLocation?.lng ? searchLocation : getClearLocation(), !onlyAvailable);
          }} showError={false} addSx={styles.checkbox()} cy="CategoriesFilterBooleanItem">
                {t('onlyAvailable', {
              product: productName || ''
            })}
              </Checkbox>}
          </>

          <div sx={styles.storesContent(column, isLoading)}>
            {!isLoading && foundShowrooms && foundShowrooms.length > 0 && foundShowrooms.map((store: IPointOfPresence) => column ? <SmallStoreCard key={store.uuid} store={store} isActive={activeUuid === store?.uuid} setActiveUuid={setActiveUuid} isDip={isDip} /> : <StoreCard key={store.uuid} store={store} userGeolocation={{
            lat: userGeolocation?.location?.lat || null,
            lng: userGeolocation?.location?.lng || null
          }} onClick={() => {
            setActiveUuid(store.uuid || '');
          }} />)}
            {isLoading && <Preloader text={t('showroomLoading')} />}
          </div>
          {!column && <div sx={styles.findAnotherButtonContainer}>
              <Link href="/showrooms">
                <Button variant="outlined" size="small">
                  {t('findAnotherStore')}
                </Button>
              </Link>
            </div>}
        </div>
      </section>
    </ErrorBoundary>;
};
export default NearestStores;